import { alpha, createTheme } from "@mui/material/styles";

/**
 * The Solution Advisor theme, taken from the Figma "Visual Design · Desktop v3" and "Mobile v3"
 * frames (file eVvI23lEykuvpyiNmixrHe; D04 Launchpad 1:2478, M04 Launchpad 1:213) and the
 * "Visual Design Style/Cue" variables (DLR Blue, DLR grays). Light only: the design has no dark scheme.
 *
 * Style cues from the design: outlined surfaces on a cool off-white page; fills are reserved for
 * active states (selected nav, primary CTA, counts); pill buttons at a 44px touch height; small
 * square-cornered badges; one type family (Inter) at fixed sizes on every breakpoint.
 */

/** Figma variable values. Components read these through the palette, not directly. */
const DLR = {
	blue: "#1F00FF",
	progressBlue: "#0040FF",
	lightBlue: "#8AAEFF",
	slate: "#01454F",
	black: "#010310",
	darkerGray: "#222222",
	darkGray: "#404040",
	bodyGray: "#555555",
	secondaryGray: "#66666F",
	midGray: "#808080",
	lightGray: "#C7C8CA",
	hairline: "#E4E5EB",
	page: "#F8F9FC",
	panel: "#F7F7F7",
	white: "#FFFFFF",
	tint: "#ECE9FF",
	count: "#E5E8FA",
	advisorTint: "#E5ECFF",
	userBubble: "#EDEDF5",
	nudge: "#FFF6D6",
	nudgeText: "#7A5A00",
	positive: "#E6F6EC",
	positiveText: "#1B7A43"
} as const;

declare module "@mui/material/styles" {
	interface Palette {
		brand: BrandPalette;
	}
	interface PaletteOptions {
		brand?: BrandPalette;
	}
}

interface BrandPalette {
	/** Panels floating over a map: DLR off-white at 88% (Figma "Metro Area Result", "Chatbot"). */
	panel: string;
	/** The featured row on the Launchpad: a faint blue tint, distinct from the page background. */
	featured: string;
	/** Stage progress bars. */
	progress: string;
	/** Highlighted (featured) card outline. */
	highlight: string;
	/** Selected nav item and other active fills. */
	selected: string;
	/** Advisor surfaces ("Ask the Advisor"). */
	tint: string;
	/** Count pills in the navigation. */
	count: string;
	/** Card and control outlines (stronger than `divider`). */
	outline: string;
	/** Secondary body copy (metadata lines under titles). */
	body: string;
	/** Initials avatars. */
	avatar: string;
	/** The advisor's spark avatar. */
	avatarTint: string;
	/** The user's turns in the advisor thread. */
	userBubble: string;
	/** The advisor composer's outline. */
	composerOutline: string;
	/** Advisor nudges and "future" badges (Figma D06 "London has no metro yet"). */
	nudge: string;
	nudgeText: string;
	/** "In your solution" confirmations. */
	positive: string;
	positiveText: string;
	/** Digital Realty metro pins on the map: the white DLR mark on this fill, as in Modeler. */
	dlrMarker: string;
}

const FONT_FAMILY = '"Inter Variable", "Helvetica Neue", Arial, sans-serif';

export const CARD_RADIUS = 16;
/** Figma map panels let the map show through at 88% opacity. */
const PANEL_OPACITY = 0.88;
/** Figma tints the featured row #F8F9FC on a white page; the app's page is already that colour. */
const FEATURED_TINT = 0.04;
export const PILL_RADIUS = 999;
export const TOUCH_HEIGHT = 44;

const base = createTheme({
	cssVariables: true,
	palette: {
		mode: "light",
		primary: { main: DLR.blue, contrastText: DLR.white },
		secondary: { main: DLR.slate, contrastText: DLR.white },
		text: { primary: DLR.darkerGray, secondary: DLR.secondaryGray },
		background: { default: DLR.page, paper: DLR.white },
		divider: DLR.hairline,
		grey: { 500: DLR.midGray },
		brand: {
			panel: alpha(DLR.panel, PANEL_OPACITY),
			featured: alpha(DLR.progressBlue, FEATURED_TINT),
			progress: DLR.progressBlue,
			highlight: DLR.lightBlue,
			selected: alpha(DLR.blue, 0.1),
			tint: DLR.tint,
			count: DLR.count,
			outline: DLR.lightGray,
			body: DLR.bodyGray,
			avatar: DLR.midGray,
			avatarTint: DLR.advisorTint,
			userBubble: DLR.userBubble,
			composerOutline: alpha(DLR.blue, 0.35),
			nudge: DLR.nudge,
			nudgeText: DLR.nudgeText,
			positive: DLR.positive,
			positiveText: DLR.positiveText,
			dlrMarker: DLR.bodyGray
		}
	},
	shape: { borderRadius: 8 },
	typography: {
		fontFamily: FONT_FAMILY,
		fontWeightRegular: 400,
		fontWeightMedium: 500,
		fontWeightBold: 600,
		// Page title ("Good morning, Maya").
		h1: { fontSize: 24, fontWeight: 600, lineHeight: 1.25 },
		// Featured card title.
		h2: { fontSize: 20, fontWeight: 500, lineHeight: "26px" },
		// Section heading ("Solutions in progress").
		h3: { fontSize: 18, fontWeight: 600, lineHeight: 1.3 },
		// Card heading ("Your Digital Realty contact").
		h4: { fontSize: 16, fontWeight: 600, lineHeight: "20px" },
		// Side-column heading ("Next steps", "Ask the Advisor").
		h5: { fontSize: 15, fontWeight: 600, lineHeight: "20px" },
		h6: { fontSize: 14, fontWeight: 600, lineHeight: "20px" },
		// List row title.
		subtitle1: { fontSize: 16, fontWeight: 500, lineHeight: "20px" },
		subtitle2: { fontSize: 14, fontWeight: 500, lineHeight: "20px" },
		body1: { fontSize: 15, lineHeight: "20px" },
		body2: { fontSize: 14, lineHeight: "18px" },
		// Metadata ("Draft saved 2 hours ago").
		caption: { fontSize: 13, lineHeight: "16px" },
		// Column and group labels ("OUTSTANDING TASKS").
		overline: { fontSize: 12, fontWeight: 600, lineHeight: "16px", letterSpacing: "0.06em" },
		button: { fontSize: 14, fontWeight: 500, lineHeight: "20px", textTransform: "none" }
	}
});

const { palette, typography } = base;

export const theme = createTheme(base, {
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				body: { backgroundColor: palette.background.default, WebkitFontSmoothing: "antialiased" },
				// Map popups (the workspace's "Add to your requirements") render as the design's cards.
				".sa-map-popup .mapboxgl-popup-content": {
					padding: 0,
					borderRadius: CARD_RADIUS,
					boxShadow: "0 8px 24px rgba(0, 0, 0, 0.16)",
					fontFamily: FONT_FAMILY
				},
				".sa-map-popup .mapboxgl-popup-tip": { display: "none" }
			}
		},
		MuiButton: {
			defaultProps: { disableElevation: true },
			styleOverrides: {
				root: {
					borderRadius: PILL_RADIUS,
					minHeight: TOUCH_HEIGHT,
					paddingInline: 22,
					variants: [
						// Outlined pills keep dark text and a blue rule ("Overview", "Book a session").
						{
							props: { variant: "outlined", color: "primary" },
							style: {
								color: palette.text.primary,
								borderColor: palette.primary.main,
								backgroundColor: palette.background.paper,
								"&:hover": { backgroundColor: palette.brand.selected, borderColor: palette.primary.main }
							}
						}
					]
				},
				sizeSmall: { minHeight: 32, paddingInline: 14, fontSize: 13 },
				text: { paddingInline: 8, minWidth: 0 }
			}
		},
		MuiIconButton: {
			styleOverrides: { root: { color: palette.text.primary } }
		},
		MuiCard: {
			defaultProps: { variant: "outlined" },
			styleOverrides: { root: { borderRadius: CARD_RADIUS, borderColor: palette.brand.outline } }
		},
		MuiCardContent: {
			styleOverrides: { root: { padding: 16, "&:last-child": { paddingBottom: 16 } } }
		},
		MuiCardActionArea: {
			styleOverrides: { root: { borderRadius: CARD_RADIUS } }
		},
		MuiPaper: {
			styleOverrides: { outlined: { borderColor: palette.brand.outline } }
		},
		// Chips render as the design's status badges: square-ish, outlined, never filled.
		MuiChip: {
			defaultProps: { variant: "outlined", size: "small" },
			styleOverrides: {
				root: { borderRadius: 4, fontSize: typography.caption.fontSize, fontWeight: 400 },
				outlined: { borderColor: palette.brand.outline, backgroundColor: palette.background.paper },
				sizeSmall: { height: 24 },
				label: { paddingInline: 8 }
			}
		},
		MuiAppBar: {
			defaultProps: { color: "inherit", elevation: 0 },
			styleOverrides: {
				root: { backgroundColor: palette.background.paper, borderBottom: `1px solid ${palette.divider}` }
			}
		},
		MuiDrawer: {
			styleOverrides: { paper: { borderColor: palette.divider } }
		},
		MuiListItemButton: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					minHeight: 40,
					paddingInline: 10,
					gap: 12,
					fontSize: 15,
					fontWeight: 500,
					"&.Mui-selected, &.active": {
						backgroundColor: palette.brand.selected,
						color: palette.primary.main,
						fontWeight: 600,
						"&:hover": { backgroundColor: alpha(palette.primary.main, 0.14) }
					}
				}
			}
		},
		MuiListItemIcon: {
			styleOverrides: { root: { minWidth: 0, color: "inherit" } }
		},
		MuiListItemText: {
			styleOverrides: { primary: { font: "inherit" } }
		},
		MuiBottomNavigation: {
			styleOverrides: { root: { height: 60, backgroundColor: palette.background.paper } }
		},
		MuiBottomNavigationAction: {
			styleOverrides: {
				root: {
					color: palette.text.secondary,
					minWidth: 0,
					gap: 4,
					"&.Mui-selected": { color: palette.primary.main }
				},
				label: {
					fontSize: 12,
					"&.Mui-selected": { fontSize: 12, fontWeight: 600 }
				}
			}
		},
		MuiTabs: {
			// Scroll rather than overflow when the tabs outgrow a phone screen.
			defaultProps: { variant: "scrollable", scrollButtons: false },
			styleOverrides: {
				root: { borderBottom: `1px solid ${palette.divider}`, minHeight: 40 },
				indicator: { height: 2 }
			}
		},
		MuiTab: {
			styleOverrides: {
				root: {
					...typography.overline,
					minHeight: 40,
					minWidth: 0,
					paddingInline: 0,
					marginRight: 40,
					color: palette.text.secondary,
					"&.Mui-selected": { color: palette.primary.main }
				}
			}
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: { backgroundColor: palette.background.paper, borderRadius: 8 },
				notchedOutline: { borderColor: palette.brand.outline }
			}
		},
		MuiAlert: {
			defaultProps: { variant: "outlined" },
			styleOverrides: {
				root: { borderRadius: CARD_RADIUS, backgroundColor: palette.background.paper, color: palette.text.primary }
			}
		},
		MuiAvatar: {
			styleOverrides: {
				colorDefault: {
					backgroundColor: palette.brand.avatar,
					color: palette.common.white,
					fontSize: 12,
					fontWeight: 500
				}
			}
		},
		MuiDialog: {
			styleOverrides: { paper: { borderRadius: CARD_RADIUS }, paperFullScreen: { borderRadius: 0 } }
		},
		MuiMenu: {
			styleOverrides: { paper: { borderRadius: 12, border: `1px solid ${palette.divider}` } }
		},
		MuiTableCell: {
			styleOverrides: {
				root: { borderColor: palette.divider },
				head: { ...typography.overline, color: palette.text.secondary }
			}
		},
		MuiLinearProgress: {
			styleOverrides: {
				root: { height: 4, borderRadius: 2, backgroundColor: palette.divider },
				bar: { borderRadius: 2, backgroundColor: palette.brand.progress }
			}
		},
		MuiBreadcrumbs: {
			styleOverrides: { root: { fontSize: typography.caption.fontSize } }
		},
		MuiLink: {
			defaultProps: { underline: "hover" }
		}
	}
});
